import React, { useState } from 'react';
import { useToast } from '../hooks/useToast';
import {
  useRoles,
  useRolesBase,
  useCrearRol,
  useActualizarRol,
  useEliminarRol,
  useActualizarPermisosRol,
  useMatrizPermisos,
} from '../hooks/useRoles';
import { useModulosActivos } from '../hooks/useModulos';
import { usePermisosActivos } from '../hooks/usePermisos';
import PageHeader from '../components/PageHeader';
import {
  Shield,
  Plus,
  Search,
  Edit,
  Trash2,
  X,
  Check,
  ChevronLeft,
  ChevronRight,
  Eye,
  FilePlus,
  FileEdit,
  FileX,
  Save,
  Info,
} from 'lucide-react';
import type { RolSistema } from '@suminuta/shared';

interface GestionRolesProps {
  embebido?: boolean;
}

const GestionRoles: React.FC<GestionRolesProps> = ({ embebido = false }) => {
  const { success, error: showError } = useToast();

  // Estados de filtros y UI
  const [filtros, setFiltros] = useState({
    page: 1,
    limit: 10,
    busqueda: '',
    soloActivos: undefined as boolean | undefined,
  });
  const [mostrarModal, setMostrarModal] = useState(false);
  const [mostrarMatriz, setMostrarMatriz] = useState(false);
  const [modoModal, setModoModal] = useState<'crear' | 'editar'>('crear');
  const [rolSeleccionado, setRolSeleccionado] = useState<RolSistema | null>(
    null
  );

  // Hooks de datos
  const { data: rolesData, isLoading } = useRoles(filtros);
  const { data: rolesBase } = useRolesBase();
  const { data: modulos } = useModulosActivos();
  const { data: permisos } = usePermisosActivos();
  const { data: matrizData } = useMatrizPermisos(rolSeleccionado?.id || 0);

  // Mutations
  const crearMutation = useCrearRol();
  const actualizarMutation = useActualizarRol();
  const eliminarMutation = useEliminarRol();
  const actualizarPermisosMutation = useActualizarPermisosRol();

  const roles = rolesData?.data || [];
  const pagination = rolesData?.pagination;

  // Handlers para CRUD de roles
  const handleCrearRol = () => {
    setRolSeleccionado(null);
    setModoModal('crear');
    setMostrarModal(true);
  };

  const handleEditarRol = (rol: RolSistema) => {
    setRolSeleccionado(rol);
    setModoModal('editar');
    setMostrarModal(true);
  };

  const handleEditarPermisos = (rol: RolSistema) => {
    setRolSeleccionado(rol);
    setMostrarMatriz(true);
  };

  const handleEliminarRol = async (rol: RolSistema) => {
    if (rol.es_rol_base) {
      showError('No se puede eliminar un rol base del sistema');
      return;
    }

    if (
      !window.confirm(
        `¿Está seguro de eliminar el rol "${rol.nombre_display}"?`
      )
    ) {
      return;
    }

    try {
      await eliminarMutation.mutateAsync(rol.id);
      success('Rol eliminado correctamente');
    } catch (error: any) {
      showError(error.response?.data?.message || 'Error al eliminar rol');
    }
  };

  const handleSubmitModal = async (data: any) => {
    try {
      if (modoModal === 'crear') {
        await crearMutation.mutateAsync(data);
        success('Rol creado correctamente');
      } else {
        await actualizarMutation.mutateAsync({
          id: rolSeleccionado!.id,
          data,
        });
        success('Rol actualizado correctamente');
      }
      setMostrarModal(false);
    } catch (error: any) {
      showError(error.response?.data?.message || `Error al ${modoModal} rol`);
    }
  };

  const handlePageChange = (newPage: number) => {
    if (newPage >= 1 && newPage <= (pagination?.totalPages || 1)) {
      setFiltros((prev) => ({ ...prev, page: newPage }));
    }
  };

  return (
    <div className={embebido ? '' : 'min-h-screen bg-gray-50 dark:bg-gray-900'}>
      {!embebido && (
        <PageHeader
          title="Gestión de Roles"
          subtitle="Administrar roles del sistema y asignar permisos"
        />
      )}

      <div
        className={
          embebido ? 'p-6' : 'max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8'
        }
      >
        {/* Información de roles base */}
        <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4 mb-6">
          <div className="flex items-start">
            <Info className="w-5 h-5 text-blue-600 dark:text-blue-400 mt-0.5 mr-3 flex-shrink-0" />
            <div>
              <h3 className="text-sm font-medium text-blue-900 dark:text-blue-100">
                Sobre los roles del sistema
              </h3>
              <p className="mt-1 text-sm text-blue-700 dark:text-blue-300">
                Los <strong>roles base</strong> (Administrador, Chef, Personal
                de Cocina, Cliente) son fundamentales del sistema y no se pueden
                eliminar. Puedes crear roles personalizados basados en estos
                roles para heredar sus permisos.
              </p>
            </div>
          </div>
        </div>

        {/* Barra de acciones */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-4 mb-6">
          <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
            {/* Buscador */}
            <div className="flex-1 max-w-md">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 dark:text-gray-500 w-5 h-5" />
                <input
                  type="text"
                  placeholder="Buscar roles..."
                  value={filtros.busqueda}
                  onChange={(e) =>
                    setFiltros((prev) => ({
                      ...prev,
                      busqueda: e.target.value,
                      page: 1,
                    }))
                  }
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
            </div>

            {/* Botón crear */}
            <button
              onClick={handleCrearRol}
              className="flex items-center gap-2 px-4 py-2 text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors"
            >
              <Plus className="w-5 h-5" />
              Nuevo Rol
            </button>
          </div>
        </div>

        {/* Tabla de roles */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm overflow-hidden">
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            </div>
          ) : roles.length === 0 ? (
            <div className="text-center py-12">
              <Shield className="mx-auto h-12 w-12 text-gray-400 dark:text-gray-500" />
              <h3 className="mt-2 text-sm font-medium text-gray-900 dark:text-gray-100">
                No hay roles
              </h3>
              <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                Comienza creando un nuevo rol personalizado.
              </p>
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                  <thead className="bg-gray-50 dark:bg-gray-700">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                        Rol
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                        Descripción
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                        Tipo
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                        Estado
                      </th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                        Acciones
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                    {roles.map((rol) => (
                      <tr
                        key={rol.id}
                        className="hover:bg-gray-50 dark:hover:bg-gray-700"
                      >
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <div className="flex-shrink-0 h-10 w-10 flex items-center justify-center bg-blue-100 dark:bg-blue-900 rounded-full">
                              <Shield className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                            </div>
                            <div className="ml-4">
                              <div className="text-sm font-medium text-gray-900 dark:text-gray-100">
                                {rol.nombre_display}
                              </div>
                              <div className="text-sm text-gray-500 dark:text-gray-400">
                                {rol.nombre}
                              </div>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <div className="text-sm text-gray-900 dark:text-gray-100">
                            {rol.descripcion || '-'}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          {rol.es_rol_base ? (
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 dark:bg-purple-900 text-purple-800 dark:text-purple-200">
                              Rol Base
                            </span>
                          ) : (
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200">
                              Personalizado
                            </span>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          {rol.activo ? (
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200">
                              <Check className="w-3 h-3 mr-1" />
                              Activo
                            </span>
                          ) : (
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 dark:bg-red-900 text-red-800 dark:text-red-200">
                              <X className="w-3 h-3 mr-1" />
                              Inactivo
                            </span>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                          <div className="flex items-center justify-end gap-2">
                            <button
                              onClick={() => handleEditarPermisos(rol)}
                              className="text-indigo-600 dark:text-indigo-400 hover:text-indigo-900 dark:hover:text-indigo-300 p-1 hover:bg-indigo-50 dark:hover:bg-indigo-900/20 rounded"
                              title="Gestionar permisos"
                            >
                              <Shield className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => handleEditarRol(rol)}
                              className="text-blue-600 dark:text-blue-400 hover:text-blue-900 dark:hover:text-blue-300 p-1 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded"
                              title="Editar rol"
                              disabled={rol.es_rol_base}
                            >
                              <Edit className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => handleEliminarRol(rol)}
                              className="text-red-600 dark:text-red-400 hover:text-red-900 dark:hover:text-red-300 p-1 hover:bg-red-50 dark:hover:bg-red-900/20 rounded disabled:opacity-50 disabled:cursor-not-allowed"
                              title="Eliminar rol"
                              disabled={rol.es_rol_base}
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Paginación */}
              {pagination && pagination.totalPages > 1 && (
                <div className="bg-white dark:bg-gray-800 px-4 py-3 border-t border-gray-200 dark:border-gray-700 sm:px-6">
                  <div className="flex items-center justify-between">
                    <div className="flex-1 flex justify-between sm:hidden">
                      <button
                        onClick={() => handlePageChange(pagination.page - 1)}
                        disabled={pagination.page === 1}
                        className="relative inline-flex items-center px-4 py-2 border border-gray-300 dark:border-gray-600 text-sm font-medium rounded-md text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50"
                      >
                        Anterior
                      </button>
                      <button
                        onClick={() => handlePageChange(pagination.page + 1)}
                        disabled={pagination.page === pagination.totalPages}
                        className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 dark:border-gray-600 text-sm font-medium rounded-md text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50"
                      >
                        Siguiente
                      </button>
                    </div>
                    <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                      <div>
                        <p className="text-sm text-gray-700 dark:text-gray-300">
                          Mostrando{' '}
                          <span className="font-medium">
                            {(pagination.page - 1) * pagination.limit + 1}
                          </span>{' '}
                          a{' '}
                          <span className="font-medium">
                            {Math.min(
                              pagination.page * pagination.limit,
                              pagination.total
                            )}
                          </span>{' '}
                          de{' '}
                          <span className="font-medium">
                            {pagination.total}
                          </span>{' '}
                          resultados
                        </p>
                      </div>
                      <div>
                        <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
                          <button
                            onClick={() =>
                              handlePageChange(pagination.page - 1)
                            }
                            disabled={pagination.page === 1}
                            className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-sm font-medium text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50"
                          >
                            <ChevronLeft className="h-5 w-5" />
                          </button>
                          <button
                            onClick={() =>
                              handlePageChange(pagination.page + 1)
                            }
                            disabled={pagination.page === pagination.totalPages}
                            className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-sm font-medium text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50"
                          >
                            <ChevronRight className="h-5 w-5" />
                          </button>
                        </nav>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* Modal crear/editar rol */}
      {mostrarModal && (
        <ModalRol
          modo={modoModal}
          rol={rolSeleccionado}
          rolesBase={rolesBase || []}
          onClose={() => setMostrarModal(false)}
          onSubmit={handleSubmitModal}
        />
      )}

      {/* Modal matriz de permisos */}
      {mostrarMatriz && rolSeleccionado && (
        <ModalMatrizPermisos
          rol={rolSeleccionado}
          modulos={modulos || []}
          permisos={permisos || []}
          matrizActual={matrizData || {}}
          onClose={() => setMostrarMatriz(false)}
          onSubmit={async (permisosData) => {
            try {
              await actualizarPermisosMutation.mutateAsync({
                rolId: rolSeleccionado.id,
                data: {
                  rol_id: rolSeleccionado.id,
                  permisos: permisosData,
                },
              });
              success('Permisos actualizados correctamente');
              setMostrarMatriz(false);
            } catch (error: any) {
              showError(
                error.response?.data?.message || 'Error al actualizar permisos'
              );
            }
          }}
        />
      )}
    </div>
  );
};

// =====================================================
// MODAL: CREAR/EDITAR ROL
// =====================================================

interface ModalRolProps {
  modo: 'crear' | 'editar';
  rol: RolSistema | null;
  rolesBase: RolSistema[];
  onClose: () => void;
  onSubmit: (data: any) => void;
}

const ModalRol: React.FC<ModalRolProps> = ({
  modo,
  rol,
  rolesBase,
  onClose,
  onSubmit,
}) => {
  const [formData, setFormData] = useState({
    nombre: rol?.nombre || '',
    nombre_display: rol?.nombre_display || '',
    descripcion: rol?.descripcion || '',
    rol_base_id: rol?.rol_base_id || undefined,
    activo: rol?.activo ?? true,
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // Agregar campo permisos vacío si es modo crear
    const dataToSubmit =
      modo === 'crear' ? { ...formData, permisos: [] } : formData;
    onSubmit(dataToSubmit);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-lg w-full">
        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
            {modo === 'crear' ? 'Nuevo Rol' : 'Editar Rol'}
          </h3>
          <button
            onClick={onClose}
            className="text-gray-400 dark:text-gray-500 hover:text-gray-500 dark:hover:text-gray-400"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Nombre interno (sin espacios)
            </label>
            <input
              type="text"
              value={formData.nombre}
              onChange={(e) =>
                setFormData((prev) => ({ ...prev, nombre: e.target.value }))
              }
              required
              pattern="[a-z_]+"
              title="Solo minúsculas y guiones bajos"
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 rounded-lg focus:ring-2 focus:ring-blue-500"
              placeholder="ej: gerente_ventas"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Nombre para mostrar
            </label>
            <input
              type="text"
              value={formData.nombre_display}
              onChange={(e) =>
                setFormData((prev) => ({
                  ...prev,
                  nombre_display: e.target.value,
                }))
              }
              required
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 rounded-lg focus:ring-2 focus:ring-blue-500"
              placeholder="ej: Gerente de Ventas"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Descripción
            </label>
            <textarea
              value={formData.descripcion}
              onChange={(e) =>
                setFormData((prev) => ({
                  ...prev,
                  descripcion: e.target.value,
                }))
              }
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 rounded-lg focus:ring-2 focus:ring-blue-500"
              placeholder="Describe las responsabilidades de este rol..."
            />
          </div>

          {modo === 'crear' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Heredar permisos de (opcional)
              </label>
              <select
                value={formData.rol_base_id || ''}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    rol_base_id: e.target.value
                      ? parseInt(e.target.value)
                      : undefined,
                  }))
                }
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 rounded-lg focus:ring-2 focus:ring-blue-500"
              >
                <option value="">Sin herencia</option>
                {rolesBase.map((rolBase) => (
                  <option key={rolBase.id} value={rolBase.id}>
                    {rolBase.nombre_display}
                  </option>
                ))}
              </select>
              <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                El nuevo rol heredará los permisos del rol seleccionado
              </p>
            </div>
          )}

          {modo === 'editar' && (
            <div className="flex items-center">
              <input
                type="checkbox"
                checked={formData.activo}
                onChange={(e) =>
                  setFormData((prev) => ({ ...prev, activo: e.target.checked }))
                }
                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 dark:border-gray-600 rounded"
              />
              <label className="ml-2 block text-sm text-gray-900 dark:text-gray-100">
                Rol activo
              </label>
            </div>
          )}

          <div className="flex gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2 text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
            >
              Cancelar
            </button>
            <button
              type="submit"
              className="flex-1 px-4 py-2 text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors"
            >
              {modo === 'crear' ? 'Crear Rol' : 'Guardar Cambios'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// =====================================================
// MODAL: MATRIZ DE PERMISOS
// =====================================================

interface ModalMatrizPermisosProps {
  rol: RolSistema;
  modulos: any[];
  permisos: any[];
  matrizActual: any;
  onClose: () => void;
  onSubmit: (permisos: any[]) => void;
}

const ModalMatrizPermisos: React.FC<ModalMatrizPermisosProps> = ({
  rol,
  modulos,
  permisos,
  matrizActual,
  onClose,
  onSubmit,
}) => {
  // Inicializar matriz de permisos
  const [matriz, setMatriz] = useState<Record<string, Set<number>>>(() => {
    const initial: Record<string, Set<number>> = {};
    modulos.forEach((modulo) => {
      const key = `${modulo.id}`;
      initial[key] = new Set();

      // Cargar permisos existentes de la API
      const moduloData = matrizActual[modulo.nombre];
      if (moduloData && moduloData.permisos) {
        permisos.forEach((permiso) => {
          // La API devuelve un objeto con permiso_nombre como clave
          if (moduloData.permisos[permiso.nombre] === true) {
            initial[key].add(permiso.id);
          }
        });
      }
    });
    return initial;
  });

  const togglePermiso = (moduloId: number, permisoId: number) => {
    setMatriz((prev) => {
      const key = `${moduloId}`;
      const newSet = new Set(prev[key]);
      if (newSet.has(permisoId)) {
        newSet.delete(permisoId);
      } else {
        newSet.add(permisoId);
      }
      return { ...prev, [key]: newSet };
    });
  };

  const toggleModuloCompleto = (moduloId: number) => {
    const key = `${moduloId}`;
    const todosSeleccionados = permisos.every((p) => matriz[key]?.has(p.id));

    setMatriz((prev) => ({
      ...prev,
      [key]: todosSeleccionados
        ? new Set()
        : new Set(permisos.map((p) => p.id)),
    }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    // Convertir matriz a formato API
    const permisosData = Object.entries(matriz)
      .filter(([_, permisoIds]) => permisoIds.size > 0)
      .map(([moduloId, permisoIds]) => ({
        modulo_id: parseInt(moduloId),
        permiso_ids: Array.from(permisoIds),
      }));

    onSubmit(permisosData);
  };

  const getIconoPermiso = (nombrePermiso: string) => {
    switch (nombrePermiso) {
      case 'ver':
        return <Eye className="w-4 h-4" />;
      case 'crear':
        return <FilePlus className="w-4 h-4" />;
      case 'editar':
        return <FileEdit className="w-4 h-4" />;
      case 'eliminar':
        return <FileX className="w-4 h-4" />;
      default:
        return null;
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-5xl w-full max-h-[90vh] flex flex-col">
        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
          <div>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
              Permisos: {rol.nombre_display}
            </h3>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
              Selecciona los permisos que tendrá este rol en cada módulo
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 dark:text-gray-500 hover:text-gray-500 dark:hover:text-gray-400"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <form
          onSubmit={handleSubmit}
          className="flex-1 flex flex-col overflow-hidden"
        >
          <div className="flex-1 overflow-auto p-6">
            <div className="overflow-x-auto">
              <table className="min-w-full border border-gray-200 dark:border-gray-700">
                <thead className="bg-gray-50 dark:bg-gray-700 sticky top-0 z-10">
                  <tr>
                    <th className="px-4 py-3 text-left text-sm font-medium text-gray-700 dark:text-gray-300 border-b border-r border-gray-200 dark:border-gray-700">
                      Módulo
                    </th>
                    {permisos.map((permiso) => (
                      <th
                        key={permiso.id}
                        className="px-4 py-3 text-center text-sm font-medium text-gray-700 dark:text-gray-300 border-b border-r border-gray-200 dark:border-gray-700"
                      >
                        <div className="flex flex-col items-center gap-1">
                          {getIconoPermiso(permiso.nombre)}
                          <span>{permiso.nombre_display}</span>
                        </div>
                      </th>
                    ))}
                    <th className="px-4 py-3 text-center text-sm font-medium text-gray-700 dark:text-gray-300 border-b border-gray-200 dark:border-gray-700">
                      Todos
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                  {modulos.map((modulo) => {
                    const key = `${modulo.id}`;
                    const todosSeleccionados = permisos.every((p) =>
                      matriz[key]?.has(p.id)
                    );

                    return (
                      <tr
                        key={modulo.id}
                        className="hover:bg-gray-50 dark:hover:bg-gray-700"
                      >
                        <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-gray-100 border-r border-gray-200 dark:border-gray-700">
                          {modulo.nombre_display}
                        </td>
                        {permisos.map((permiso) => (
                          <td
                            key={permiso.id}
                            className="px-4 py-3 text-center border-r border-gray-200 dark:border-gray-700"
                          >
                            <input
                              type="checkbox"
                              checked={matriz[key]?.has(permiso.id) || false}
                              onChange={() =>
                                togglePermiso(modulo.id, permiso.id)
                              }
                              className="h-5 w-5 text-blue-600 focus:ring-blue-500 border-gray-300 dark:border-gray-600 rounded cursor-pointer"
                            />
                          </td>
                        ))}
                        <td className="px-4 py-3 text-center">
                          <input
                            type="checkbox"
                            checked={todosSeleccionados}
                            onChange={() => toggleModuloCompleto(modulo.id)}
                            className="h-5 w-5 text-green-600 focus:ring-green-500 border-gray-300 dark:border-gray-600 rounded cursor-pointer"
                          />
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>

          <div className="flex gap-3 p-6 border-t border-gray-200 dark:border-gray-700">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2 text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
            >
              Cancelar
            </button>
            <button
              type="submit"
              className="flex-1 flex items-center justify-center gap-2 px-4 py-2 text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors"
            >
              <Save className="w-5 h-5" />
              Guardar Permisos
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default GestionRoles;
